import { Badge, Box, Button, Center, Flex, Loader, Paper, Stack, Text } from '@mantine/core';
import { IconAirConditioning, IconChevronRight, IconPlugConnected, IconThermometer } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import panic from 'panic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AIR_CONDITIONER_DETAIL_PAGE_PATH } from 'routes/paths';
import { SyncDaikinDevicesResponse } from 'api/actions/sync-daikin-devices/sync-daikin-devices-response';
import useLang from 'lang';

type AirConditioner = SyncDaikinDevicesResponse['airConditioners'][number];

interface DeviceWithStatus extends AirConditioner {
  isOn?: boolean | null;
  roomTemperature?: number | null;
  operationMode?: string | null;
  statusLoaded: boolean;
}

const MODE_LABELS: Record<string, string> = {
  cooling: 'Cooling',
  heating: 'Heating',
  fanOnly: 'Ventilation',
  auto: 'Auto',
};

/**
 * Air Conditioner landing page.
 *
 * Flow:
 * 1. Check Daikin auth status.
 * 2. If not connected — show "Connect" button.
 * 3. If connected — sync devices, fetch statuses in parallel, auto-redirect if 1 device.
 * 4. On every tab focus re-sync to pick up new devices and re-check auth.
 */
export default function AirConditionerLanding() {
  const { getAction } = useApi();
  const { _t } = useLang();
  const navigate = useNavigate();

  const [authLoading, setAuthLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [devices, setDevices] = useState<DeviceWithStatus[]>([]);
  const syncInFlight = useRef(false);

  /** Syncs devices from Daikin — status is included directly in the sync response, no extra calls. */
  const syncDevices = useCallback(() => {
    if (syncInFlight.current) return;
    syncInFlight.current = true;
    setSyncLoading(true);
    setSyncError(null);
    const syncDaikinDevices = getAction('SyncDaikinDevices');
    syncDaikinDevices({ payload: {} })
      .then(({ airConditioners }) => {
        if (airConditioners.length === 1) {
          navigate(AIR_CONDITIONER_DETAIL_PAGE_PATH.insert({ airConditionerId: String(airConditioners[0].id) }));
          return;
        }
        const withStatus: DeviceWithStatus[] = airConditioners.map((ac: AirConditioner) => ({
          ...ac,
          isOn: ac.status?.isOn ?? null,
          roomTemperature: ac.status?.roomTemperature ?? null,
          operationMode: ac.status?.operationMode ?? null,
          statusLoaded: true,
        }));
        setDevices(withStatus);
      })
      .catch((err: any) => {
        const status = err?.response?.status;
        if (status === 400 || status === 401) {
          setIsConnected(false);
        } else if (status === 429) {
          setSyncError(_t('Daikin API is overloaded. Try again in a moment.'));
        } else {
          panic(err);
        }
      })
      .finally(() => {
        syncInFlight.current = false;
        setSyncLoading(false);
      });
  }, [_t, getAction, navigate]);

  /** On mount: check auth and sync once. On tab-visible: re-sync only if we just came back from OAuth (hidden→visible after connect). */
  const checkAndSync = useCallback(() => {
    const getDaikinAuthStatus = getAction('GetDaikinAuthStatus');
    getDaikinAuthStatus({})
      .then(({ connected }) => {
        setIsConnected(connected);
        if (connected) syncDevices();
      })
      .catch(panic)
      .finally(() => setAuthLoading(false));
  }, [getAction, syncDevices]);

  useEffect(() => {
    checkAndSync();
    /** Re-sync only when returning from OAuth (tab was hidden during auth redirect). */
    let wasHidden = false;
    /** Re-syncs when tab becomes visible after being hidden (e.g. after OAuth redirect). */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        wasHidden = true;
      } else if (document.visibilityState === 'visible' && wasHidden) {
        wasHidden = false;
        checkAndSync();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkAndSync]);

  /** Starts Daikin OAuth flow in a new tab. */
  const handleConnect = useCallback(() => {
    setIsConnecting(true);
    const startDaikinAuth = getAction('StartDaikinAuth');
    startDaikinAuth({ payload: {} })
      .then(({ url }) => {
        window.location.href = url;
      })
      .catch(panic)
      .finally(() => setIsConnecting(false));
  }, [getAction]);

  if (authLoading || syncLoading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" color="#551FFF" />
          <Text c="dimmed" size="sm">
            {_t('Loading devices...')}
          </Text>
        </Stack>
      </Center>
    );
  }

  if (!isConnected) {
    return (
      <Stack bg="gray.0" py={20} px={16} mih="100vh" align="center" gap={4}>
        <Paper py="20px" px="16px" w="100%" bg="white" style={{ borderRadius: '10px' }}>
          <Stack align="center" gap={16}>
            <IconPlugConnected size={48} color="#551FFF" />
            <Text fw={700} size="xl" ta="center">
              {_t('Connect air conditioning to Daikin')}
            </Text>
            <Text c="dimmed" ta="center" size="sm">
              {_t('Click the button and sign in to your Daikin Onecta account.')}
            </Text>
            <Button
              fullWidth
              size="lg"
              radius="xl"
              leftSection={<IconPlugConnected size={20} />}
              loading={isConnecting}
              onClick={handleConnect}
              style={{ backgroundColor: '#551FFF', color: 'white' }}
            >
              {_t('Connect with Daikin')}
            </Button>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack bg="gray.0" py={20} px={16} mih="100vh" gap={12}>
      {/* Header */}
      <Flex justify="space-between" align="center" px={2}>
        <Text fw={700} size="xl">
          {_t('Air conditioners')}
        </Text>
        <Badge color="violet" variant="light" size="lg" radius="xl">
          {devices.length}
        </Badge>
      </Flex>

      {/* Rate limit warning */}
      {syncError && (
        <Paper py="12px" px="16px" bg="orange.1" style={{ borderRadius: '10px' }}>
          <Text c="orange.8" size="sm" ta="center">
            {syncError}
          </Text>
        </Paper>
      )}

      {/* Device list */}
      <Stack gap={8}>
        {devices.map((device) => {
          const isOn = device.isOn;
          return (
            <Paper
              key={device.id}
              shadow="xs"
              style={{
                borderRadius: 14,
                cursor: 'pointer',
                border: isOn ? '1.5px solid rgba(85,31,255,0.15)' : '1.5px solid #E9EBEC',
                background: 'white',
              }}
              onClick={() => navigate(AIR_CONDITIONER_DETAIL_PAGE_PATH.insert({ airConditionerId: String(device.id) }))}
            >
              <Flex align="center" gap={14} px={16} py={14}>
                {/* Icon */}
                <Box
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 14,
                    flexShrink: 0,
                    background: isOn ? '#F0EBFF' : '#F5F6F7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconAirConditioning size={26} color={isOn ? '#551FFF' : '#AFB5C1'} />
                </Box>

                {/* Info */}
                <Stack gap={3} style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    fw={600}
                    size="md"
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {device.name}
                  </Text>

                  {device.statusLoaded ? (
                    <Flex align="center" gap={6}>
                      <Box
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          background: isOn ? '#22C55E' : '#AFB5C1',
                          ...(isOn ? { boxShadow: '0 0 0 3px rgba(34,197,94,0.2)' } : {}),
                        }}
                      />
                      <Text size="sm" c={isOn ? '#22C55E' : 'dimmed'} fw={500}>
                        {isOn ? _t('Active') : _t('Off')}
                      </Text>
                      {isOn && device.operationMode && (
                        <Text size="sm" c="dimmed">
                          · {_t(MODE_LABELS[device.operationMode] ?? device.operationMode)}
                        </Text>
                      )}
                    </Flex>
                  ) : (
                    <Text size="sm" c="dimmed">
                      {_t('Loading status...')}
                    </Text>
                  )}

                  {device.statusLoaded && device.roomTemperature != null && (
                    <Flex align="center" gap={4}>
                      <IconThermometer size={13} color="#AFB5C1" />
                      <Text size="xs" c="dimmed">
                        {_t('Room temperature')}: {device.roomTemperature}°C
                      </Text>
                    </Flex>
                  )}
                </Stack>

                {/* Chevron */}
                <Box style={{ color: '#AFB5C1', flexShrink: 0 }}>
                  <IconChevronRight size={20} />
                </Box>
              </Flex>
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
}
